import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/category/all?search=${encodeURIComponent(searchQuery.trim())}`;
    }
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm border-b border-background-200/70' : 'bg-transparent'}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className={`text-xl md:text-2xl font-heading font-bold tracking-wide ${isScrolled ? 'text-foreground-950' : 'text-white'}`}>
              韩国漫画网
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${location.pathname === '/' ? (isScrolled ? 'bg-primary-100 text-primary-700' : 'bg-white/20 text-white') : (isScrolled ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50' : 'text-white/80 hover:text-white hover:bg-white/10')}`}
            >
              首页
            </Link>
            {categories.slice(0, 5).map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${location.pathname === `/category/${cat.slug}` ? (isScrolled ? 'bg-primary-100 text-primary-700' : 'bg-white/20 text-white') : (isScrolled ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50' : 'text-white/80 hover:text-white hover:bg-white/10')}`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${location.pathname === '/about' ? (isScrolled ? 'bg-primary-100 text-primary-700' : 'bg-white/20 text-white') : (isScrolled ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50' : 'text-white/80 hover:text-white hover:bg-white/10')}`}
            >
              关于我们
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${isScrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer"
              aria-label="菜单"
            >
              <i className={`text-lg ${isScrolled ? 'text-foreground-700' : 'text-white'}`}>{isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}</i>
            </button>
          </div>
        </div>
      </div>

      {isSearchOpen && (
        <div className="absolute top-full left-0 right-0 bg-white border-t border-background-200/70 shadow-lg p-4">
          <form onSubmit={handleSearch} className="max-w-xl mx-auto flex items-center gap-2">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索漫画名称、作者..."
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-full focus:outline-none focus:border-primary-400 transition-colors"
                autoFocus
              />
            </div>
            <button type="submit" className="px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              搜索
            </button>
          </form>
        </div>
      )}

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200/70 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            <Link to="/" className="block px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg whitespace-nowrap">首页</Link>
            {categories.map((cat) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className="block px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg whitespace-nowrap">{cat.name}</Link>
            ))}
            <Link to="/about" className="block px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg whitespace-nowrap">关于我们</Link>
          </div>
        </div>
      )}
    </nav>
  );
}